'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Heart, Share2, BarChart2, MapPin, Bed, Bath, Maximize, Eye, TrendingDown, Box, Video, CheckCircle } from 'lucide-react';
import SavePropertyButton from '@/components/SavePropertyButton';

export interface PropertyCardData {
  id: string;
  title: string;
  price: string;
  pricePerM2: string;
  location: string;
  district: string;
  city: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  type: string;
  listingType: 'satilik' | 'kiralik';
  image: string;
  imageAlt: string;
  badges: Array<'yeni' | 'fiyat-dustu' | '3d' | 'one-cikan' | 'dogrulanmis' | 'video'>;
  views: number;
  daysListed: number;
  rentalYield?: string;
  investmentScore?: number;
  isNew?: boolean;
  agent?: string;
}

interface PropertyCardProps {
  property: PropertyCardData;
  variant?: 'grid' | 'list';
  onFavorite?: (id: string) => void;
  onCompare?: (id: string) => void;
}

export default function PropertyCard({ property, variant = 'grid', onFavorite, onCompare }: PropertyCardProps) {
  const [isFavorited, setIsFavorited] = useState(false);
  const [isComparing, setIsComparing] = useState(false);

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    onFavorite?.(property.id);
  };

  const handleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsComparing(!isComparing);
    onCompare?.(property.id);
  };

  const badgeMap = {
    'yeni': <span className="badge-new">Yeni İlan</span>,
    'fiyat-dustu': <span className="badge-price-drop flex items-center gap-1"><TrendingDown size={10} />Fiyat Düştü</span>,
    '3d': <span className="badge-3d flex items-center gap-1"><Box size={10} />3D Tur</span>,
    'one-cikan': <span className="badge-featured">Öne Çıkan</span>,
    'dogrulanmis': <span className="badge-verified flex items-center gap-1"><CheckCircle size={10} />Doğrulandı</span>,
    'video': <span className="badge-3d flex items-center gap-1"><Video size={10} />Video</span>,
  };

  if (variant === 'list') {
    return (
      <Link href="/property-detail" className="block">
        <div className="card-base hover-lift overflow-hidden flex gap-0 group cursor-pointer">
          <div className="relative w-56 flex-shrink-0 overflow-hidden">
            <AppImage
              src={property.image}
              alt={property.imageAlt}
              width={224}
              height={168}
              className="w-full h-42 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute top-2 left-2 flex flex-wrap gap-1">
              {property.badges.slice(0, 2).map((badge) => (
                <span key={`badge-list-${property.id}-${badge}`}>{badgeMap[badge]}</span>
              ))}
            </div>
            <span className={`absolute bottom-2 left-2 text-xs font-bold px-2 py-0.5 rounded-md ${property.listingType === 'satilik' ? 'bg-primary text-white' : 'bg-accent text-white'}`}>
              {property.listingType === 'satilik' ? 'Satılık' : 'Kiralık'}
            </span>
          </div>
          <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
            <div>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-base font-bold text-foreground line-clamp-1">{property.title}</h3>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={handleFavorite} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 ${isFavorited ? 'bg-danger-light text-danger' : 'bg-muted text-muted-foreground hover:bg-danger-light hover:text-danger'}`}>
                    <Heart size={15} fill={isFavorited ? 'currentColor' : 'none'} />
                  </button>
                  <SavePropertyButton
                    property={{
                      propertyId: property.id,
                      propertyTitle: property.title,
                      propertyPrice: property.price,
                      propertyLocation: `${property.district}, ${property.city}`,
                      propertyType: property.type,
                      listingType: property.listingType,
                    }}
                    variant="icon"
                    className="!w-8 !h-8 !rounded-lg !shadow-none bg-muted text-muted-foreground hover:!bg-primary-light hover:!text-primary"
                  />
                  <button onClick={handleCompare} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 ${isComparing ? 'bg-primary-light text-primary' : 'bg-muted text-muted-foreground hover:bg-primary-light hover:text-primary'}`}>
                    <BarChart2 size={15} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground text-sm mb-3">
                <MapPin size={13} className="text-primary flex-shrink-0" />
                <span>{property.district}, {property.city}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                <span className="flex items-center gap-1"><Bed size={14} className="text-primary" />{property.bedrooms}+1</span>
                <span className="flex items-center gap-1"><Bath size={14} className="text-primary" />{property.bathrooms}</span>
                <span className="flex items-center gap-1"><Maximize size={14} className="text-primary" />{property.area} m²</span>
                <span className="text-xs text-muted-foreground">{property.type}</span>
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xl font-bold text-foreground tabular-nums">{property.price}</p>
                <p className="text-xs text-muted-foreground tabular-nums">{property.pricePerM2}/m²</p>
              </div>
              {property.rentalYield && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Kira Getirisi</p>
                  <p className="text-sm font-bold text-success">%{property.rentalYield}</p>
                </div>
              )}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Eye size={12} />
                <span>{property.views.toLocaleString('tr-TR')}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link href="/property-detail" className="block">
      <div className="card-base hover-lift overflow-hidden group cursor-pointer h-full flex flex-col">
        {/* Image */}
        <div className="relative overflow-hidden aspect-[4/3]">
          <AppImage
            src={property.image}
            alt={property.imageAlt}
            width={400}
            height={300}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-1">
            {property.badges.slice(0, 3).map((badge) => (
              <span key={`badge-${property.id}-${badge}`}>{badgeMap[badge]}</span>
            ))}
          </div>

          {/* Listing type */}
          <span className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-lg ${property.listingType === 'satilik' ? 'bg-primary text-white' : 'bg-accent text-white'}`}>
            {property.listingType === 'satilik' ? 'Satılık' : 'Kiralık'}
          </span>

          {/* Action buttons - visible on hover */}
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={handleFavorite}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 shadow-md ${isFavorited ? 'bg-danger text-white' : 'bg-white text-foreground hover:bg-danger hover:text-white'}`}
              aria-label="Favorilere ekle"
            >
              <Heart size={14} fill={isFavorited ? 'currentColor' : 'none'} />
            </button>
            <SavePropertyButton
              property={{
                propertyId: property.id,
                propertyTitle: property.title,
                propertyPrice: property.price,
                propertyLocation: `${property.district}, ${property.city}`,
                propertyType: property.type,
                listingType: property.listingType,
              }}
              variant="icon"
            />
            <button
              onClick={handleCompare}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 shadow-md ${isComparing ? 'bg-primary text-white' : 'bg-white text-foreground hover:bg-primary hover:text-white'}`}
              aria-label="Karşılaştırmaya ekle"
            >
              <BarChart2 size={14} />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              className="w-8 h-8 rounded-lg bg-white text-foreground hover:bg-secondary flex items-center justify-center transition-all duration-150 shadow-md"
              aria-label="Paylaş"
            >
              <Share2 size={14} />
            </button>
          </div>

          {/* Investment score badge */}
          {property.investmentScore !== undefined && (
            <div className={`absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 rounded-lg text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${property.investmentScore >= 75 ? 'investment-score-high' : property.investmentScore >= 50 ? 'investment-score-medium' : 'investment-score-low'}`}>
              <BarChart2 size={11} />
              {property.investmentScore}/100
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-xl font-bold text-foreground tabular-nums leading-tight">{property.price}</p>
              <p className="text-xs text-muted-foreground tabular-nums">{property.pricePerM2}/m²</p>
            </div>
            {property.rentalYield && (
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Kira Getirisi</p>
                <p className="text-sm font-bold text-success">%{property.rentalYield}</p>
              </div>
            )}
          </div>

          <h3 className="text-sm font-semibold text-foreground line-clamp-1 mb-1">{property.title}</h3>

          <div className="flex items-center gap-1 text-muted-foreground text-xs mb-3">
            <MapPin size={11} className="text-primary flex-shrink-0" />
            <span>{property.district}, {property.city}</span>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground pt-3 border-t border-border mt-auto">
            <span className="flex items-center gap-1"><Bed size={12} className="text-primary" />{property.bedrooms}+1</span>
            <span className="flex items-center gap-1"><Bath size={12} className="text-primary" />{property.bathrooms}</span>
            <span className="flex items-center gap-1"><Maximize size={12} className="text-primary" />{property.area} m²</span>
            <span className="ml-auto flex items-center gap-1 text-muted-foreground">
              <Eye size={11} />
              {property.views.toLocaleString('tr-TR')}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}